import { EMOTE_IDS } from '@il-colpo/shared';
import { z } from 'zod';
import { ActionSchema } from './action.ts';

/**
 * Everything the client and the server say to each other, as zod schemas checked on both sides.
 * The one thing not checked field by field is the view the server sends (`PlayerView`): it is the
 * server's own output, typed by the engine, and a second schema of it would only drift. Its
 * envelope (`seq`, an object) is checked.
 */

export const ROOM_NAME = 'heist';

export const LanguageSchema = z.enum(['it', 'en']);

export const RoleSchema = z.enum(['mastermind', 'locksmith', 'hacker', 'lookout', 'driver']);

/** What the host of a room decides (private rooms) and what a quick-match room is made with. */
export const LobbyConfigSchema = z.strictObject({
  mapId: z.enum(['bank', 'museum', 'villa', 'express']),
  difficulty: z.enum(['easy', 'normal', 'pro', 'legend']),
  infiltrator: z.enum(['never', 'always', 'normal']),
  /** The language of the password vocabulary (GDD §16.23). */
  language: LanguageSchema,
  /** Seats at the table, bots included: 3, 4 or 5. */
  size: z.union([z.literal(3), z.literal(4), z.literal(5)]),
  /** Whether bots fill the seats that no player takes. */
  bots: z.boolean(),
  /** Private rooms: the players pick their roles in turn, in a random order, instead of being dealt them. */
  draft: z.boolean(),
  /** Replaces the nominal execution timer (private rooms), in milliseconds. */
  timerMs: z
    .number()
    .int()
    .min(60_000)
    .max(30 * 60_000)
    .optional(),
});
export type LobbyConfig = z.infer<typeof LobbyConfigSchema>;

export const DEFAULT_LOBBY_CONFIG: LobbyConfig = {
  mapId: 'bank',
  difficulty: 'normal',
  infiltrator: 'normal',
  language: 'it',
  size: 4,
  bots: true,
  draft: false,
};

/** What a player says when joining or creating a room (the options of the Colyseus call). */
export const JoinOptionsSchema = z.strictObject({
  protocolVersion: z.number().int(),
  /** The Supabase access token of the player (or, in development, a development token). */
  token: z.string().min(1).max(8192),
  /** The language of the player's interface; a quick match is made among players of one language. */
  language: LanguageSchema,
  mode: z.enum(['quick', 'private', 'ranked', 'daily']),
  /** Only when creating a private room. */
  config: LobbyConfigSchema.partial().optional(),
  /** The version of the app (`major.minor.patch`), so that a server can turn away one it no longer serves. */
  clientVersion: z.string().max(32).optional(),
});
export type JoinOptions = z.infer<typeof JoinOptionsSchema>;

// ---- Client → server --------------------------------------------------------------------

export const ClientMessages = {
  /** In the lobby: "I am ready" (private rooms). */
  ready: z.strictObject({ ready: z.boolean() }),
  /** The host changes the room's settings. */
  config: LobbyConfigSchema.partial(),
  /** The host starts the heist. */
  start: z.strictObject({}),
  kick: z.strictObject({ playerId: z.string().min(1).max(16) }),
  /** In the role draft: "I take this role" (only when it is the player's turn). */
  pick: z.strictObject({ role: RoleSchema }),
  /** An action of the engine; the server decides who is acting. */
  intent: z.strictObject({ action: ActionSchema }),
  chat: z.strictObject({ text: z.string().min(1).max(400) }),
  /** An emote (GDD §13.3): a picture to the table. Open to every age, to the muted too. */
  emote: z.strictObject({ id: z.enum(EMOTE_IDS) }),
} as const;

export type ClientMessageType = keyof typeof ClientMessages;
export type ClientMessageOf<K extends ClientMessageType> = z.infer<(typeof ClientMessages)[K]>;

/** The longest chat message, after trimming. */
export const CHAT_MAX_LENGTH = 200;

// ---- Server → client --------------------------------------------------------------------

export const ErrorCodeSchema = z.enum([
  'protocolMismatch',
  'unauthorized',
  'roomFull',
  'notFound',
  'gameInProgress',
  'alreadyInRoom',
  'notHost',
  'badRequest',
  'rateLimited',
  'illegal',
  'chatForbidden',
  'notEnoughPlayers',
  'consentRequired',
  /** Ranked play: left a heist not long ago (the detail is how many seconds are left). */
  'queueBanned',
  /** Ranked play needs a real account, not a guest. */
  'accountRequired',
  /** The mode is switched off for now (a feature flag). */
  'featureOff',
  /** The daily heist: the day's three attempts are used. */
  'noAttempts',
  /** The account is banned from online play (the detail is the seconds left, or `forever`). */
  'banned',
  /** The account is muted, so no free chat (the detail is the seconds left, or `forever`). */
  'muted',
  /** The server could not do it now (a service it depends on failed): try again in a moment. */
  'unavailable',
  /** The app is older than the oldest the server still serves. */
  'updateRequired',
]);
export type ErrorCode = z.infer<typeof ErrorCodeSchema>;

const AvatarSchema = z.record(z.string(), z.string().max(32));

export const LobbyPlayerSchema = z.strictObject({
  /** The seat: `p1`…`p5`, the id the engine knows the player by. */
  id: z.string().min(1).max(16),
  /** `Nickname#0042`. */
  name: z.string().min(1).max(40),
  avatar: AvatarSchema,
  ready: z.boolean(),
  host: z.boolean(),
  connected: z.boolean(),
  /** A bot holds the seat (no player, or a player who has dropped). */
  bot: z.boolean(),
  /** Whether the player may type free text (age band). */
  freeChat: z.boolean(),
});
export type LobbyPlayer = z.infer<typeof LobbyPlayerSchema>;

/** The role draft of a private room, which everybody at the table sees (a pick is public by nature). */
export const DraftSchema = z.strictObject({
  /** The seats, in the order in which they pick. */
  order: z.array(z.string().min(1).max(16)).max(5),
  /** Whose turn it is. */
  turn: z.string().min(1).max(16).nullable(),
  /** What has been picked so far. */
  picks: z.record(z.string().min(1).max(16), RoleSchema),
  /** The roles still to be taken. */
  free: z.array(RoleSchema).max(5),
  /** Milliseconds left for the one whose turn it is (counted down on the client from the moment the message arrived). */
  msLeft: z.number().int().nonnegative(),
});
export type Draft = z.infer<typeof DraftSchema>;

export const RoomPhaseSchema = z.enum(['lobby', 'playing', 'over']);
export type RoomPhase = z.infer<typeof RoomPhaseSchema>;

/**
 * What a finished heist did for the player who is told (their own and nobody else's): the XP and where
 * it came from, the level, the rating of a ranked game, the challenges that moved, the trophies and
 * cosmetics earned. It is sent as soon as the server has settled the heist, and is the same
 * `PlayerSettlement` of `packages/meta` that the database is given.
 */
export const SettlementSummarySchema = z.strictObject({
  matchId: z.string().min(1).max(64),
  xp: z.strictObject({
    participation: z.number().int(),
    success: z.number().int(),
    win: z.number().int(),
    unmask: z.number().int(),
    infiltratorWin: z.number().int(),
    firstOfDay: z.number().int(),
    total: z.number().int(),
  }),
  challengeXp: z.number().int(),
  xpGain: z.number().int(),
  /** The player's XP after the heist, in all. */
  xpTotal: z.number().int(),
  levelBefore: z.number().int().positive(),
  levelAfter: z.number().int().positive(),
  rating: z
    .strictObject({
      kind: z.enum(['thief', 'infiltrator']),
      displayBefore: z.number().int(),
      displayAfter: z.number().int(),
      placed: z.boolean(),
      games: z.number().int(),
    })
    .nullable(),
  challenges: z
    .array(
      z.strictObject({
        id: z.string(),
        progress: z.number().int(),
        goal: z.number().int(),
        completed: z.boolean(),
        rewardXp: z.number().int(),
      }),
    )
    .max(16),
  trophies: z.array(z.string()).max(32),
  cosmetics: z.array(z.string()).max(32),
});
export type SettlementSummary = z.infer<typeof SettlementSummarySchema>;

export const ServerMessages = {
  hello: z.strictObject({
    protocolVersion: z.number().int(),
    /** The seat of the player. */
    you: z.string().min(1).max(16),
    /** The six characters that name the room (the code of a private room). */
    code: z.string().min(1).max(16),
    mode: z.enum(['quick', 'private', 'ranked', 'daily']),
  }),
  lobby: z.strictObject({
    phase: RoomPhaseSchema,
    host: z.string().min(1).max(16),
    config: LobbyConfigSchema,
    players: z.array(LobbyPlayerSchema).max(5),
    /** Quick match: the time left before the empty seats are filled and the heist starts. */
    startsInMs: z.number().int().nullable(),
    /** Ranked: how many more players the table needs before the wait for the last seat begins. */
    needed: z.number().int().nonnegative().nullable(),
    /** A private room whose host has started the heist with the draft on: the picking, until it is over. */
    draft: DraftSchema.nullable(),
  }),
  view: z.strictObject({
    seq: z.number().int().nonnegative(),
    /** The player's own `PlayerView` (typed by the engine; see the note above). */
    view: z.looseObject({}),
  }),
  chat: z.strictObject({
    from: z.string().min(1).max(16),
    name: z.string().min(1).max(40),
    text: z
      .string()
      .min(1)
      .max(CHAT_MAX_LENGTH * 2),
    at: z.number().int(),
  }),
  emote: z.strictObject({
    from: z.string().min(1).max(16),
    name: z.string().min(1).max(40),
    id: z.enum(EMOTE_IDS),
    at: z.number().int(),
  }),
  error: z.strictObject({
    code: ErrorCodeSchema,
    message: z.string().max(200).optional(),
  }),
  kicked: z.strictObject({}),
  settlement: SettlementSummarySchema,
} as const;

export type ServerMessageType = keyof typeof ServerMessages;
export type ServerMessageOf<K extends ServerMessageType> = z.infer<(typeof ServerMessages)[K]>;

// ---- Room codes -------------------------------------------------------------------------

/** No 0/O, 1/I/L: a code read out loud or from a screen is not mistaken. */
export const ROOM_CODE_ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
export const ROOM_CODE_LENGTH = 6;

export function isRoomCode(text: string): boolean {
  return text.length === ROOM_CODE_LENGTH && [...text].every((c) => ROOM_CODE_ALPHABET.includes(c));
}

/** What a player typed, as a code: upper case, spaces and dashes gone. */
export function normalizeRoomCode(text: string): string {
  return text.toUpperCase().replace(/[\s-]/g, '');
}
